import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  createPeriodicTask,
  previewPeriodicTask,
  updatePeriodicTask,
} from "../../../distflowAPI/periodicTasksApi";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./scheduledTasks.css";
import ScheduleFields from "./ScheduleFields";
import {
  changedFields,
  displayTime,
  errorMessage,
  newInterval,
} from "./scheduleHelpers";

export default function TaskEditor({
  item,
  tasks,
  timezones,
  onClose,
  onSaved,
}) {
  const [draft, setDraft] = useState(() => ({
    name: item?.name || "",
    task: item?.task || "",
    description: item?.description || "",
    enabled: item?.enabled ?? false,
    args: item?.args || [],
    kwargs: item?.kwargs || {},
    schedule: item?.schedule || newInterval(),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [advanced, setAdvanced] = useState(Boolean(item?.args?.length));
  const [argsText, setArgsText] = useState(JSON.stringify(draft.args, null, 2));
  const [kwargsText, setKwargsText] = useState(
    JSON.stringify(draft.kwargs, null, 2)
  );
  const selected = tasks.find((task) => task.name === draft.task);
  const taskOptions =
    selected || !draft.task
      ? tasks
      : [
          ...tasks,
          {
            name: draft.task,
            label: `${draft.task} (unavailable)`,
            parameters: [],
          },
        ];
  const change = (key, value) =>
    setDraft((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    let active = true;
    setPreview(null);
    setPreviewError("");
    const timer = setTimeout(() => {
      previewPeriodicTask(draft.schedule)
        .then(({ data }) => {
          if (active) setPreview(data);
        })
        .catch((err) => {
          if (active) setPreviewError(errorMessage(err));
        });
    }, 350);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [draft.schedule]);

  async function save(event) {
    event.preventDefault();
    setError("");
    let payload = { ...draft };
    if (advanced) {
      try {
        payload.args = JSON.parse(argsText);
        payload.kwargs = JSON.parse(kwargsText);
        if (
          !Array.isArray(payload.args) ||
          !payload.kwargs ||
          Array.isArray(payload.kwargs) ||
          typeof payload.kwargs !== "object"
        )
          throw new Error();
      } catch (_) {
        setError(
          "Arguments must be a JSON array and keyword arguments must be a JSON object."
        );
        return;
      }
    }
    setSaving(true);
    try {
      if (item) await updatePeriodicTask(item.id, changedFields(item, payload));
      else await createPeriodicTask(payload);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function changeParameter(parameter, value) {
    const kwargs = { ...draft.kwargs };
    if (value === "") delete kwargs[parameter.name];
    else
      kwargs[parameter.name] =
        parameter.type === "number" ? Number(value) : value;
    change("kwargs", kwargs);
  }
  const complexParameters = selected?.parameters?.some(
    (parameter) => parameter.positional_only || parameter.type === "json"
  );
  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      className="schedule-editor"
      aria-labelledby="schedule-editor-title"
    >
      <Box component="form" onSubmit={save} className="schedule-editor-form">
        <DialogTitle
          id="schedule-editor-title"
          className="schedule-editor-header"
        >
          <Box>
            <Typography component="span" variant="h6">
              {item ? "Edit scheduled task" : "Create scheduled task"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose what runs, when it runs, and review the next occurrences.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close editor"
            disabled={saving}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="schedule-editor-content">
          <Box className="schedule-editor-grid">
            <Stack spacing={2.5} className="schedule-editor-main">
              {error ? <Alert severity="error">{error}</Alert> : null}
              <Typography
                variant="subtitle2"
                className="schedule-section-title"
              >
                Task details
              </Typography>
              <TextField
                label="Name"
                required
                value={draft.name}
                onChange={(e) => change("name", e.target.value)}
              />
              <Autocomplete
                componentsProps={{ popper: { sx: { zIndex: 1600 } } }}
                options={taskOptions}
                value={
                  taskOptions.find((task) => task.name === draft.task) || null
                }
                getOptionLabel={(task) => task.label}
                isOptionEqualToValue={(a, b) => a.name === b.name}
                filterOptions={(options, { inputValue }) =>
                  options.filter((task) =>
                    `${task.label} ${task.name}`
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  )
                }
                renderOption={(props, task) => (
                  <li {...props} key={task.name}>
                    <Box>
                      <Typography>{task.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.name}
                      </Typography>
                    </Box>
                  </li>
                )}
                onChange={(_, task) => {
                  setDraft((current) => ({
                    ...current,
                    task: task?.name || "",
                    args: [],
                    kwargs: {},
                  }));
                  setArgsText("[]");
                  setKwargsText("{}");
                  setAdvanced(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Task"
                    required
                    helperText={
                      draft.task || "Search for the task you want to schedule."
                    }
                  />
                )}
              />
              <Box className="schedule-timing-section">
                <Typography
                  variant="subtitle2"
                  className="schedule-section-title"
                  sx={{ mb: 2 }}
                >
                  When should it run?
                </Typography>
                <ScheduleFields
                  schedule={draft.schedule}
                  onChange={(value) => change("schedule", value)}
                  timezones={timezones}
                />
              </Box>
              <Accordion
                key={draft.task}
                defaultExpanded={Boolean(
                  selected?.parameters?.length ||
                    draft.args.length ||
                    Object.keys(draft.kwargs).length
                )}
                disableGutters
                elevation={0}
                className="schedule-extra"
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" fontWeight={600}>
                    Description & task parameters
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <TextField
                      label="Description"
                      multiline
                      minRows={2}
                      value={draft.description}
                      onChange={(e) => change("description", e.target.value)}
                    />
                    {selected?.parameters?.length ||
                    advanced ||
                    Object.keys(draft.kwargs).length ||
                    draft.args.length ? (
                      <Stack spacing={2}>
                        <Typography variant="subtitle2">
                          Task parameters
                        </Typography>
                        {complexParameters ? (
                          <Typography variant="body2">
                            This task has structured parameters. Use the JSON
                            fields below for those values.
                          </Typography>
                        ) : null}
                        {!advanced
                          ? selected?.parameters
                              ?.filter(
                                (param) =>
                                  !param.positional_only &&
                                  param.type !== "json"
                              )
                              .map((param) =>
                                param.type === "boolean" ? (
                                  <FormControlLabel
                                    key={param.name}
                                    label={param.name.replace(/_/g, " ")}
                                    control={
                                      <Switch
                                        checked={
                                          draft.kwargs[param.name] ??
                                          param.default ??
                                          false
                                        }
                                        onChange={(_, checked) =>
                                          changeParameter(param, checked)
                                        }
                                      />
                                    }
                                  />
                                ) : (
                                  <TextField
                                    key={param.name}
                                    label={param.name.replace(/_/g, " ")}
                                    required={param.required}
                                    type={
                                      param.type === "date"
                                        ? "date"
                                        : param.type === "number"
                                          ? "number"
                                          : "text"
                                    }
                                    InputLabelProps={
                                      param.type === "date"
                                        ? { shrink: true }
                                        : undefined
                                    }
                                    value={draft.kwargs[param.name] ?? ""}
                                    helperText={
                                      param.required
                                        ? "Required"
                                        : `Optional${param.default !== null ? ` · default: ${param.default}` : ""}`
                                    }
                                    onChange={(e) =>
                                      changeParameter(param, e.target.value)
                                    }
                                  />
                                )
                              )
                          : null}
                        <Button
                          size="small"
                          sx={{ alignSelf: "flex-start" }}
                          onClick={() => {
                            if (!advanced) {
                              setArgsText(JSON.stringify(draft.args, null, 2));
                              setKwargsText(
                                JSON.stringify(draft.kwargs, null, 2)
                              );
                              setAdvanced(true);
                            }
                          }}
                          disabled={advanced}
                        >
                          Advanced JSON parameters
                        </Button>
                        {advanced ? (
                          <>
                            <TextField
                              label="Arguments (JSON array)"
                              multiline
                              minRows={2}
                              value={argsText}
                              onChange={(e) => setArgsText(e.target.value)}
                            />
                            <TextField
                              label="Keyword arguments (JSON object)"
                              multiline
                              minRows={3}
                              value={kwargsText}
                              onChange={(e) => setKwargsText(e.target.value)}
                            />
                          </>
                        ) : null}
                      </Stack>
                    ) : null}
                  </Stack>
                </AccordionDetails>
              </Accordion>
              {item?.start_time ||
              item?.expires ||
              item?.one_off ||
              item?.queue ? (
                <Alert severity="info">
                  This task also has advanced settings from Django admin. Its
                  start/expiry limits, one-off behavior, and queue routing are
                  preserved.
                </Alert>
              ) : null}
            </Stack>
            <Stack spacing={2} className="schedule-editor-sidebar">
              <Box
                className="schedule-preview"
                component="aside"
                aria-label="Schedule preview"
              >
                <Typography variant="subtitle2" gutterBottom>
                  Schedule preview
                </Typography>
                {previewError ? (
                  <Alert severity="error">{previewError}</Alert>
                ) : preview ? (
                  <>
                    <Typography variant="body2" fontWeight={600}>
                      {preview.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Next 5 runs
                      {draft.schedule.timezone
                        ? ` · ${draft.schedule.timezone}`
                        : " · interval measured from now"}
                      .
                    </Typography>
                    <Box
                      component="ol"
                      sx={{ pl: 0, mb: 0, listStyle: "none" }}
                    >
                      {preview.next_runs.map((time, index) => (
                        <Typography
                          component="li"
                          variant="body2"
                          key={index}
                          sx={{
                            py: 1,
                            borderBottom: "1px solid #e2e6ef",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {displayTime(time, draft.schedule.timezone)}
                        </Typography>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Checking schedule…</Typography>
                  </Stack>
                )}
              </Box>

              <Box className="schedule-enabled">
                <FormControlLabel
                  label="Enable automatic runs"
                  control={
                    <Switch
                      checked={draft.enabled}
                      onChange={(_, value) => change("enabled", value)}
                    />
                  }
                />
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                >
                  {draft.enabled
                    ? "This task will run automatically on the schedule above."
                    : "Saved as paused. Enable it when you are ready for automatic runs."}
                </Typography>
              </Box>
              <Alert severity="warning" sx={{ fontSize: 12 }}>
                Changes affect live operations. Check the timing before saving.
              </Alert>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !draft.task}
          >
            {saving ? "Saving…" : item ? "Save schedule" : "Create schedule"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
